<script>
import moment from 'moment';

export default {
  name: 'Event',
  props: {
    id: { type: Number, default: null },
    dates: Array,
    title: String,
    description: String,
    organisation: String,
    link: String,
    featured: Boolean,
    status: { type: String, default: null },
    editMode: { type: Boolean, default: false }
  },
  data: () => {
    return {
      showDescription: false
    };
  },
  methods: {
    formatDate(date) {
      return moment(new Date(date)).format('DD.MM.YYYY');
    },
    toggleDescription() {
      this.showDescription = !this.showDescription;
    },
    onEdit() {
      this.$router.push(this.$route.path + '/event/edit/' + this.id);
    },
    onDelete() {
      this.$router.push(this.$route.path + '/event/delete/' + this.id);
    }
  },
  computed: {
    dateShort() {
      return moment(new Date(this.dates[0].date)).format('D.M.');
    },
    statusLabel() {
      return this.status === 'draft' ? this.$t('event.statusDraft') : '';
    }
  }
};
</script>

<template>
  <article
    class="event"
    v-bind:class="[
      { 'event--featured': featured },
      { 'event--editable': editMode },
    ]"
  >
    <div class="event__date">
      <span class="event__date__from" v-if="dates.length > 1"
        >{{ $t('event.from') }}
      </span>
      <span class="event__date__date">{{ dateShort }}</span>
    </div>
    <div class="event__content-wrapper">
      <div class="event__content">
        <h1 class="event__title">
          <span v-html="title"></span>
          <span class="event__status" v-if="statusLabel">{{
            statusLabel
          }}</span>
        </h1>
        <p class="event__description" v-if="description && showDescription">
          {{ description }}
        </p>
        <p class="event__organisation">{{ organisation }}</p>
        <ul
          class="event__content__dates"
          v-bind:class="{
            'event__content__dates--needs-space':
              dates.length > 1 || showDescription,
          }"
        >
          <li
            class="event__content__date"
            v-for="(date, i) in dates"
            v-bind:key="i"
          >
            <span>{{ formatDate(date.date) }}</span>
            <span v-if="date.time">, {{ date.time }}</span>
            <span v-if="date.location">, {{ date.location }}</span>
          </li>
        </ul>
        <a v-bind:href="link" class="event__link read-more" target="_blank">{{
          $t('event.moreInfo')
        }}</a>
        <button
          class="event__description-toggle"
          v-if="description"
          v-on:click="toggleDescription"
          v-bind:class="{ 'event__description-toggle--open': showDescription }"
        ></button>
        <ul class="event__actions" v-if="editMode">
          <li class="event__action">
            <button
              class="event__action-btn event__action-btn--edit"
              v-on:click="onEdit()"
            ></button>
          </li>
          <li class="event__action">
            <button
              class="event__action-btn event__action-btn--delete"
              v-on:click="onDelete()"
            ></button>
          </li>
        </ul>
      </div>
    </div>
  </article>
</template>

<style lang="scss" scoped>
.event {
  position: relative;
  display: flex;
  box-sizing: border-box;
  min-height: 11.6rem;
  padding: 0.96rem 0 3.2rem;
  border-bottom: 0.1rem solid rgba($color-primary, 0.15);
}

.event__date {
  width: 14.4rem;
  min-width: 14.4rem;
  font-weight: 700;

  @include breakpoint('xs') {
    min-width: 11.2rem;
  }
}
.event__date__from {
  font-size: 3rem;
  line-height: 1;
}
.event__date__date {
  font-size: 4.6rem;
  line-height: 1;

  @include breakpoint('xs') {
    font-size: 3.68rem;
  }
}

.event__content-wrapper {
  width: 100%;
  overflow: hidden;
}
.event__content {
  max-width: 56rem;
}
.event--editable .event__content {
  padding-right: 12rem;
}
.event__title {
  font-size: 2.08rem;
  font-weight: normal;
  letter-spacing: 0.057rem;
  line-height: 1.15;
  margin: 0 0 0.32rem;

  @include breakpoint('xs') {
    margin: 0 0 0.8rem 0;
  }
}
.event__description {
  margin: 0 0 0.96rem;
  line-height: 1.3;
  opacity: 0.7;
}
.event__organisation {
  margin: 0;
  line-height: 1.3;
}
.event__link read-more {
  display: block;
  margin: 1.28rem 0 0;
}

.event__content__dates {
  margin: 0 0;
  padding: 0;
}
.event__content__dates--needs-space {
  margin: 0.8rem 0 0;
}
.event__content__date {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.5;

  @include breakpoint('xs') {
    white-space: normal;
    margin: 0.8rem 0;
  }
}

.event__description-toggle {
  position: absolute;
  top: 1.6rem;
  right: 0;
  width: 3.36rem;
  height: 3.36rem;
  margin: -1.68rem 0;
  border: none;
  outline: none;
  background: center url(~@/assets/images/ico-chevron-bottom-gray.svg) no-repeat;
  cursor: pointer;
}
.event__description-toggle--open {
  transform: rotate(180deg);
}

.event__actions {
  position: absolute;
  top: 0.96rem;
  right: 4rem;
  display: flex;
  margin: 0 -0.4rem;
  padding: 0;
}
.event__action {
  display: block;
  margin: 0 0.4rem;
}
.event__action-btn {
  width: 2.4rem;
  height: 2.4rem;
  outline: none;
  border: none;
  margin: 0;
  padding: 0;
  background: center transparent no-repeat;
  cursor: pointer;
}
.event__action-btn--edit {
  background-image: url('~@/assets/images/ico-edit.svg');
}
.event__action-btn--delete {
  background-image: url('~@/assets/images/ico-delete.svg');
}
</style>
